import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RefreshProjectProps } from "../../types/screensTypes";
import { useGlobalState } from "../../context/useGlobalState";
import { addNewsletterItemsToProject } from "../../api";
import Dropdown from "../../components/common/Dropdown";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import TextInput from "../../components/ui/TextInput";
import Button from "../../components/ui/Button";
import styles from "./AddItem.module.css";

interface FieldErrors {
    selectedContentLanguage: boolean;
    singleTitle: boolean;
    singleText: boolean;
}

const AddItemToNewsletterProject: React.FC<RefreshProjectProps> = ({ refreshProjects }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data, loading, error } = useGlobalState();

    // ---------------------------
    // Shared state
    // ---------------------------
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [singleTitle, setSingleTitle] = useState<string>("");
    const [singleText, setSingleText] = useState<string>("");
    const [showErrors, setShowErrors] = useState<boolean>(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
        selectedContentLanguage: false,
        singleTitle: false,
        singleText: false,
    });
    const [isGenerateButtonDisabled, setIsGenerateButtonDisabled] = useState<boolean>(true);

    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // ---------------------------
    // Validation
    // ---------------------------
    useEffect(() => {
        const errors: FieldErrors = {
            selectedContentLanguage: !selectedContentLanguage,
            singleTitle: !singleTitle.trim(),
            singleText: !singleText.trim(),
        };

        setFieldErrors(errors);
        setIsGenerateButtonDisabled(Object.values(errors).some((err) => err === true));
    }, [selectedContentLanguage, singleTitle, singleText]);

    const showErrorMessage = () => {
        setShowErrors(true);
        setTimeout(() => {
            setShowErrors(false);
        }, 3000);
    };

    // ---------------------------
    // Submit / Save
    // ---------------------------
    const saveItem = async () => {
        setIsLoading(true);

        if (!selectedContentLanguage || !singleTitle.trim() || !singleText.trim()) {
            setIsLoading(false);
            setShowErrors(true);
            setTimeout(() => setShowErrors(false), 3000);
            return;
        }

        try {
            const token = localStorage.getItem("token");

            if (!token || !id) {
                setIsLoading(false);
                return;
            }

            const resp = await addNewsletterItemsToProject({
                token,
                projectId: id,
                language: selectedContentLanguage,
                textItems: [{ title: singleTitle, text: singleText }],
            });

            if (resp && resp.message) {
                navigate(`/newsletter-projects/${id}`);
            }
            setIsLoading(false);
        } catch (error) {
            console.error("Failed to save item:", error);
            setIsLoading(false);
        }
    };

    // ---------------------------
    // Render
    // ---------------------------
    return (
        <div className={styles.page}>
            <div className={styles.stack}>
                <div className={styles.field}>
                    <TitleSubtitle
                        title={t("inWhichLanguageWouldYouLikeAddNewsletter")}
                        subtitle=""
                        addMargin={false}
                    />
                    <Dropdown
                        label=""
                        options={data.languagesAI}
                        onChange={setSelectedContentLanguage}
                        loading={loading}
                        error={error ? true : false}
                        placeholder={t("language")}
                        value={selectedContentLanguage}
                        isMissing={showErrors && fieldErrors.selectedContentLanguage}
                        isWithoutMargin
                    />
                </div>

                {selectedContentLanguage && (
                    <>
                        <div className={styles.field}>
                            <TitleSubtitle title={t("whatIsTheTitleOfYourStory")} subtitle="" addMargin={false} />
                            <TextInput
                                placeholder={t("pleaseInsertYourText")}
                                value={singleTitle}
                                onChange={(e) => setSingleTitle(e.target.value)}
                                size={"medium"}
                                isMissing={showErrors && fieldErrors.singleTitle}
                            />
                        </div>

                        <div className={styles.field}>
                            <TitleSubtitle
                                title={t("textEdition")}
                                subtitle={t("textEditionSubNewsletter")}
                                addMargin={false}
                            />
                            <TextInput
                                placeholder={t("pleaseInsertYourText")}
                                value={singleText}
                                onChange={(e) => setSingleText(e.target.value)}
                                size={"big"}
                                numberOfLines={12}
                                isMissing={showErrors && fieldErrors.singleText}
                            />
                        </div>

                        <div className={styles.actions}>
                            <Button
                                label={isLoading ? t("processing") : t("add")}
                                onClick={saveItem}
                                variant="primary"
                                disabled={isGenerateButtonDisabled}
                                showError={showErrorMessage}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AddItemToNewsletterProject;
